//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { updateBvProductPdpUrl } from '@apis/universal-image-actions/universalImageActionsApi';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';

interface UpdatePdpUrlRequest {
  externalId: string;
  pdpUrl: string;
}

export const useUpdateProductPdpUrlMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ externalId, pdpUrl }: UpdatePdpUrlRequest) =>
      updateBvProductPdpUrl(externalId, pdpUrl),
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'PDP URL Updated',
          message: "You've successfully updated the PDP URL.",
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Failed to update PDP URL',
          message:
            'An unexpected error has occurred. Try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.UniversalImageActions,
          QueryKeyConstants.UniversalImageActionsProducts,
          QueryKeyConstants.UniversalImageActionsProductsProductPdpUrl,
        ],
      });
    },
  });
};
